/**
 * Shared contract between the viewer engines (IfcViewer, GlbViewer, DxfViewer)
 * and the viewer UI (ViewerWorkspace).
 *
 * Engines own the 3D/2D scene: camera, selection, visibility, colours, x-ray,
 * clipping, measurements and walk mode. The workspace owns the UI, notes and
 * viewpoints, and merges everything into a ViewState that is saved to Firestore
 * and replayed on shared links.
 */

export type Vec3 = [number, number, number]

export type ViewerKind = 'ifc' | 'glb' | 'dxf'

/** Axis that points up in the source data. The mesh engine rotates Z-up data to Y-up. */
export type UpAxis = 'y' | 'z'

/** Capabilities an engine supports; the workspace only shows matching tools. */
export type ViewerFeature =
  | 'tree' // model structure (IFC spatial tree, GLB node hierarchy)
  | 'groups' // IFC categories / DXF layers, toggleable
  | 'properties' // element properties on selection
  | 'visibility' // hide / isolate / show all
  | 'colors' // paint selected elements
  | 'xray' // semi-transparent model
  | 'clipping' // section planes
  | 'measure' // point-to-point distance
  | 'notes' // picking points for anchored notes
  | 'walk' // first-person navigation
  | 'projection' // perspective / orthographic toggle
  | 'presets' // top / front / side / iso cameras
  | 'frame' // user-adjustable units and up axis (formats without reliable units)

/** Interaction mode set by the workspace toolbar. */
export type ViewerTool = 'select' | 'measure' | 'note' | 'clip'

export type CameraPreset = 'iso' | 'top' | 'front' | 'back' | 'left' | 'right'

export type Projection = 'perspective' | 'orthographic'

export type ClipAxis = 'x' | 'y' | 'z'

export interface CameraState {
  position: Vec3
  target: Vec3
  projection?: Projection
  /** DXF only: visible width of the 2D view, in drawing units. */
  width?: number
}

export interface ColorOverride {
  /** Element ids (IFC GlobalIds, GLB node paths). */
  ids: string[]
  /** '#rrggbb' */
  color: string
}

export interface ClipPlaneState {
  /** Unit normal; the visible side is the one the normal points away from (Three.js convention). */
  normal: Vec3
  point: Vec3
}

export interface MeasurementState {
  id: string
  a: Vec3
  b: Vec3
}

export interface NoteState {
  id: string
  position: Vec3
  text: string
  /** Element the note is attached to, when picked on one. */
  elementId?: string
  createdAt: number
}

export interface ViewpointState {
  id: string
  name: string
  camera: CameraState
}

/**
 * Everything the owner can save. Engines read/write the scene parts;
 * notes and viewpoints are handled by the workspace.
 */
export interface ViewState {
  version: 1
  camera?: CameraState
  /** Hidden element ids. */
  hidden?: string[]
  /** When set, only these elements are visible. */
  isolated?: string[] | null
  /** Hidden group ids (IFC categories, DXF layers). */
  hiddenGroups?: string[]
  colors?: ColorOverride[]
  xray?: boolean
  clipping?: ClipPlaneState[]
  measurements?: MeasurementState[]
  notes?: NoteState[]
  viewpoints?: ViewpointState[]
  /** Metres per model unit chosen by the owner (feature 'frame'). */
  unitScale?: number
  /** Up axis chosen by the owner (feature 'frame'). Applied before every other world coordinate. */
  upAxis?: UpAxis
  /** DXF files with 3D content: which engine the owner left it in. */
  display?: '2d' | '3d'
}

/** The scene-related subset of ViewState an engine produces in getState(). */
export type EngineState = Omit<ViewState, 'version' | 'notes' | 'viewpoints'>

export interface TreeNode {
  id: string
  name: string
  /** IFC class (IfcWall…) or node type. */
  category?: string
  children: TreeNode[]
  /** False for pure containers (site, storey, empty groups). */
  hasGeometry: boolean
}

export interface GroupInfo {
  id: string
  name: string
  count: number
  /** '#rrggbb' swatch (DXF layer colour). */
  color?: string
}

export interface PropertyGroup {
  name: string
  props: [string, string][]
}

export interface SelectionInfo {
  ids: string[]
  /** e.g. 'IfcBeam' or the node name. */
  title: string
  /** e.g. element Name, or '3 elements' for multi-selection. */
  subtitle?: string
  groups: PropertyGroup[]
}

export interface MeasurementInfo extends MeasurementState {
  /** Distance in metres (engines convert from their native units). */
  length: number
}

export interface ScreenPoint {
  /** Pixels relative to the viewer container's top-left corner. */
  x: number
  y: number
  /** False when behind the camera or outside the viewport. */
  visible: boolean
}

export interface PickResult {
  point: Vec3
  elementId?: string
}

/**
 * Methods and reactive state each engine exposes via defineExpose().
 * Reactive members are refs inside the engine; through a template ref they
 * read as plain values (Vue unwraps them on the component proxy).
 */
export interface ViewerApi {
  readonly kind: ViewerKind
  readonly features: ViewerFeature[]
  readonly ready: boolean

  // Camera
  fitView(): void
  setPreset(preset: CameraPreset): void
  toggleProjection(): void
  readonly projection: Projection
  getCamera(): CameraState
  setCamera(camera: CameraState, animate?: boolean): Promise<void>
  /** First-person navigation on/off (feature 'walk'). */
  setWalk(on: boolean): void
  readonly walking: boolean

  // Interaction
  setTool(tool: ViewerTool): void
  readonly tool: ViewerTool

  // Selection / properties
  readonly selection: SelectionInfo | null
  selectIds(ids: string[]): Promise<void>
  clearSelection(): void
  /** Frames the current selection. */
  focusSelection(): void

  // Visibility
  hideSelected(): Promise<void>
  isolateSelected(): Promise<void>
  showAll(): Promise<void>
  /** True when anything is hidden or isolated (elements or groups). */
  readonly visibilityModified: boolean
  readonly groups: GroupInfo[]
  readonly hiddenGroups: string[]
  setGroupVisible(groupId: string, visible: boolean): Promise<void>
  loadTree(): Promise<TreeNode[]>
  setNodesVisible(ids: string[], visible: boolean): Promise<void>

  // Appearance
  colorSelected(hex: string): Promise<void>
  clearColors(): Promise<void>
  readonly hasColors: boolean
  readonly xray: boolean
  setXray(on: boolean): Promise<void>

  // Clipping
  addClipPlane(axis: ClipAxis): void
  clearClipping(): void
  readonly clipCount: number

  // Measurements
  readonly measurements: MeasurementInfo[]
  removeMeasurement(id: string): void
  clearMeasurements(): void

  // Overlay helpers (notes and measurement labels are DOM elements drawn by the workspace)
  project(points: Vec3[]): ScreenPoint[]

  // Units and up axis (feature 'frame'; other engines report fixed values)
  readonly unitScale: number
  readonly upAxis: UpAxis
  /**
   * Changes units and/or up axis. Changing the up axis rotates the model, so the
   * engine moves its own measurements, clipping planes and camera with rotateUp();
   * the workspace does the same for notes and viewpoints.
   */
  setFrame(frame: { unitScale?: number; upAxis?: UpAxis }): Promise<void>

  // State
  getState(): EngineState
  applyState(state: ViewState): Promise<void>

  // Images
  /** Full-resolution PNG of the current view. */
  screenshot(): Promise<Blob | null>
  /** 1200x630 JPEG for og:image (existing thumbnail flow). */
  captureSnapshot(): Promise<Blob | null>
}

/**
 * Events every engine emits:
 * - 'loaded'          model ready
 * - 'pick'            (PickResult) a click in 'note' tool mode
 * - 'changed'         the scene state changed through user interaction (marks the view dirty)
 * - 'measurement-added' (MeasurementInfo)
 */
export interface ViewerEvents {
  loaded: []
  pick: [result: PickResult]
  changed: []
  'measurement-added': [measurement: MeasurementInfo]
}

export const EMPTY_STATE: ViewState = { version: 1 }

/**
 * Moves a world point when the up axis changes from `from` to `to`.
 * Z-up data is shown rotated -90° about X: (x, y, z) -> (x, z, -y).
 */
export function rotateUp(p: Vec3, from: UpAxis, to: UpAxis): Vec3 {
  if (from === to) return p
  return to === 'z' ? [p[0], p[2], -p[1]] : [p[0], -p[2], p[1]]
}

export function newId(): string {
  return Math.random().toString(36).slice(2, 10)
}
